/**
 * Centralized Configuration Module for Opera Sidecar
 *
 * All URLs are read from environment variables — no hardcoded hostnames here.
 *
 * SECURITY: This file does NOT contain tokens, secrets, or credentials.
 * The SSO access token (from Microsoft Entra via WSO2) is managed by
 * react-oidc-context and attached to requests by apiClient.
 *
 * Usage:
 *   import config from './config';
 *   const url = config.operaExpBaseUrl;
 */

/**
 * Normalize a URL by trimming whitespace and removing trailing slashes
 */
function normalizeUrl(url) {
  if (!url) return '';
  return url.trim().replace(/\/+$/, '');
}

/**
 * Normalize router basename by ensuring it starts with /
 */
function normalizeBasename(basename) {
  if (!basename) return '/';
  const trimmed = basename.trim();
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

/**
 * Get required environment variable with validation
 */
function getRequiredEnvVar(key, friendlyName) {
  const value = process.env[key];
  
  if (!value || value.trim() === '') {
    const errorMsg = `❌ Missing required environment variable: ${key} (${friendlyName})`;
    console.error(errorMsg);
    
    // In production, this is critical
    if (process.env.NODE_ENV === 'production') {
      throw new Error(errorMsg);
    }
    
    // In development, warn but allow to continue
    console.warn(`⚠️ Using empty value for ${key}. Application may not work correctly.`);
    return '';
  }
  
  return value.trim();
}

/**
 * Get optional environment variable with default
 */
function getOptionalEnvVar(key, defaultValue) {
  const value = process.env[key];
  return value ? value.trim() : (defaultValue || '');
}

// Environment Detection
const isProduction = process.env.NODE_ENV === 'production';
const isDevelopment = process.env.NODE_ENV === 'development';
const isDebugEnabled = getOptionalEnvVar('REACT_APP_DEBUG', isDevelopment ? 'true' : 'false') === 'true';

// Router Configuration
// Local: '/', QA/Prod: matches hosted subpath if any
const routerBasename = normalizeBasename(
  getOptionalEnvVar('REACT_APP_ROUTER_BASENAME', '/')
);

// Opera EXP API base URL — all member/loyalty REST calls go here directly.
// Local dev: http://localhost:3000/opera-exp  (CRA proxy forwards to real API)
// QA/Prod:   set via REACT_APP_OPERA_EXP_BASE_URL in the appropriate env file
const operaExpBaseUrl = normalizeUrl(
  getRequiredEnvVar('REACT_APP_OPERA_EXP_BASE_URL', 'Opera EXP API Base URL')
);

// Hard Rock Logo - local asset bundled with the app
const logoUrl = '/assets/hardrock-logo.png';

// ── OIDC / SSO Configuration (WSO2 → Microsoft Entra ID) ────────────────────
// All OIDC vars are optional here; authConfig.js reads them directly from
// process.env and handles missing-value warnings itself.
const oidcAuthority              = getOptionalEnvVar('REACT_APP_OIDC_AUTHORITY', '');
const oidcClientId               = getOptionalEnvVar('REACT_APP_OIDC_CLIENT_ID', '');
const oidcRedirectUri            = getOptionalEnvVar('REACT_APP_OIDC_REDIRECT_URI', '');
const oidcScope                  = getOptionalEnvVar('REACT_APP_OIDC_SCOPE', 'openid profile email');
const oidcPostLogoutRedirectUri  = getOptionalEnvVar('REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI', '');

// Export as a single configuration object
const config = {
  // Environment
  envName: isProduction ? 'production' : 'development',
  isProduction: isProduction,
  isDevelopment: isDevelopment,
  isDebug: isDebugEnabled,
  
  // Router
  routerBasename: routerBasename,

  // Opera EXP API — frontend calls this directly using the SSO Bearer token.
  // Value comes from REACT_APP_OPERA_EXP_BASE_URL in the env file.
  operaExpBaseUrl: operaExpBaseUrl,

  // ── SSO / OIDC (WSO2 → Entra ID) ────────────────────────────────────────
  oidcAuthority,
  oidcClientId,
  oidcRedirectUri,
  oidcScope,
  oidcPostLogoutRedirectUri,

  // Assets
  logoUrl: logoUrl,
};

// Debug logging (only in development)
if (config.isDebug) {
  // Debug logging disabled to avoid printing configuration values in console.
}

// Export default config object
export default config;
